import { useMemo, useState } from 'react';

import type { AnomalyFlagItem } from '../services/types';

interface AnomaliesTableProps {
  anomalies: AnomalyFlagItem[];
  onAck: (anomalyId: string) => Promise<void>;
}

const severityClass: Record<string, string> = {
  low: 'bg-surface-alt text-muted',
  medium: 'bg-warn/15 text-warn',
  high: 'bg-danger/15 text-danger',
  critical: 'bg-danger/30 text-danger font-semibold',
};

interface ColumnIssueGroup {
  column: string;
  issue: string;
  count: number;
  acknowledged: number;
}

// v0.5 Stage C.1 — anomaly band aggregation.
// Now that cleansers route the [10%, 50%] invalid-fraction band to the
// Anomalies tab instead of a blocking HITL, runs can carry hundreds of
// anomaly flags. Surface a compact column×issue summary so the reviewer
// notices the column-quality signal without scanning every row.
function groupByColumnAndIssue(anomalies: AnomalyFlagItem[]): ColumnIssueGroup[] {
  const buckets = new Map<string, ColumnIssueGroup>();
  for (const a of anomalies) {
    const key = `${a.column}::${a.issue}`;
    const existing = buckets.get(key);
    if (existing) {
      existing.count += 1;
      if (a.acknowledged) existing.acknowledged += 1;
    } else {
      buckets.set(key, {
        column: a.column,
        issue: a.issue,
        count: 1,
        acknowledged: a.acknowledged ? 1 : 0,
      });
    }
  }
  return Array.from(buckets.values()).sort((a, b) => {
    if (a.column !== b.column) return a.column.localeCompare(b.column);
    return b.count - a.count;
  });
}

export function AnomaliesTable({ anomalies, onAck }: AnomaliesTableProps) {
  const [acking, setAcking] = useState<Set<string>>(new Set());

  const groups = useMemo(() => groupByColumnAndIssue(anomalies), [anomalies]);

  if (anomalies.length === 0) {
    return (
      <p className="text-sm text-muted py-4">
        No anomaly flags were raised for this run.
      </p>
    );
  }

  const handleAck = async (anomalyId: string) => {
    setAcking((s) => new Set(s).add(anomalyId));
    try {
      await onAck(anomalyId);
    } finally {
      setAcking((s) => {
        const next = new Set(s);
        next.delete(anomalyId);
        return next;
      });
    }
  };

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted">
        {anomalies.length} anomaly flag{anomalies.length === 1 ? '' : 's'}.{' '}
        {anomalies.filter((a) => a.acknowledged).length > 0 &&
          `${anomalies.filter((a) => a.acknowledged).length} acknowledged.`}
      </p>

      {groups.length > 1 && (
        <section
          aria-label="Column-quality summary"
          data-testid="anomaly-column-summary"
          className="rounded-md border border-border bg-surface-alt/40 p-3"
        >
          <h3 className="text-xs font-medium uppercase tracking-wide text-muted mb-2">
            By column
          </h3>
          <ul className="space-y-1 text-sm">
            {groups.map((g) => (
              <li
                key={`${g.column}::${g.issue}`}
                className="flex items-center justify-between gap-2"
              >
                <span className="font-mono text-xs">{g.column}</span>
                <span className="flex-1 text-xs text-muted truncate ml-2">
                  {g.issue}
                </span>
                <span className="text-xs font-medium tabular-nums">
                  {g.count} row{g.count === 1 ? '' : 's'}
                  {g.acknowledged > 0 && (
                    <span className="text-muted"> ({g.acknowledged} ack)</span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-surface-alt text-muted text-xs uppercase">
              <th className="px-3 py-2 text-left font-medium w-16">Row</th>
              <th className="px-3 py-2 text-left font-medium w-28">Column</th>
              <th className="px-3 py-2 text-left font-medium">Original</th>
              <th className="px-3 py-2 text-left font-medium">Issue</th>
              <th className="px-3 py-2 text-left font-medium">Attempted</th>
              <th className="px-3 py-2 text-left font-medium">Agent</th>
              <th className="px-3 py-2 text-left font-medium w-20">Severity</th>
              <th className="px-3 py-2 text-left font-medium w-24">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {anomalies.map((a) => {
              // row_id=-1 is the *sole* column-level summary sentinel
              // (e.g. missing_handler's null_values_present). Strict
              // ``=== -1`` is deliberate — ``!row_id`` / ``row_id <= 0``
              // would catch ``0`` which is a valid first-row position in
              // pre-Stage-B3 callers and not what we want to flag as a
              // summary. Render "summary" in the row column and surface
              // the ``detail`` text under the Issue cell.
              const isSummary =
                typeof a.row_id === 'number' && a.row_id === -1;
              return (
              <tr
                key={a.anomaly_id}
                className={`hover:bg-surface-alt/40 ${a.acknowledged ? 'opacity-60' : ''}`}
                data-testid={isSummary ? 'anomaly-summary-row' : 'anomaly-row'}
              >
                <td className="px-3 py-2 font-mono text-xs text-muted">
                  {isSummary ? <em className="opacity-60">summary</em> : a.row_id}
                </td>
                <td className="px-3 py-2 font-mono text-xs">{a.column}</td>
                <td className="px-3 py-2 text-xs text-muted max-w-[12rem] break-words">
                  {a.original_value || <em className="opacity-40">empty</em>}
                </td>
                <td className="px-3 py-2 text-xs">
                  {a.issue}
                  {isSummary && a.detail && (
                    <span className="block text-muted text-[11px] mt-0.5">
                      {a.detail}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-xs text-muted max-w-[10rem] break-words">
                  {a.attempted_transformation ?? <em className="opacity-40">—</em>}
                </td>
                <td className="px-3 py-2 text-xs font-mono text-muted">
                  {a.responsible_agent ?? '—'}
                </td>
                <td className="px-3 py-2">
                  {a.severity && (
                    <span
                      className={`text-xs px-2 py-0.5 rounded-sm ${
                        severityClass[a.severity] ?? 'bg-surface-alt text-muted'
                      }`}
                    >
                      {a.severity}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2">
                  {a.acknowledged ? (
                    <span className="text-xs text-muted">acknowledged</span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => void handleAck(a.anomaly_id)}
                      disabled={acking.has(a.anomaly_id)}
                      className="text-xs text-brand-dark underline hover:no-underline disabled:text-muted disabled:no-underline"
                    >
                      {acking.has(a.anomaly_id) ? 'Saving…' : 'Acknowledge'}
                    </button>
                  )}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
